import { visaTabs } from "const/constants";
import { matchesVisaTab } from "const/visaCategories";
import { P } from "const/permissions";
import {
  fetchInsuranceClientsPage,
  fetchInsuranceRecordsByClientId,
  fetchRplClientsPage,
  fetchRplRecordsByClientId,
  fetchSkillClientsPage,
  fetchSkillRecordsByClientId,
  fetchVisaClientsPage,
  fetchVisaRecordsByClientId,
} from "./moduleClientListApi";

const RPL_STATUS_TABS = [
  "All",
  "Incomplete Application",
  "Application Ready",
  "Enrollment Applied",
  "Payment Completed",
  "Enrollment Received",
  "Placement Waiting",
  "Placement Processing",
  "Ready to Apply",
  "Certificate Processing",
  "Certificate Ready",
  "Softcopy sent",
  "Hardcopy sent",
  "Cancelled",
];

const formatEntryDate = (item) =>
  item?.updatedDate ?? item?.updatedAt ?? item?.createdDate ?? item?.createdAt ?? null;

// Migrated records sometimes carry a stray boolean-ish value ("true"/"false") in the status column.
// That is never a real status label, so drop it instead of rendering it as a tag or in the subtitle.
const cleanStatus = (value) => {
  if (value == null) return null;
  const str = String(value).trim();
  if (!str) return null;
  const lower = str.toLowerCase();
  return lower === "true" || lower === "false" ? null : str;
};

export const MODULE_CLIENT_CONFIGS = {
  rpl: {
    key: "rpl",
    title: "RPL · Clients",
    subtitle: "Open a client to see all their RPL certificates.",
    entryCountLabel: "RPL records",
    entriesSectionLabel: "RPL certificates",
    basePath: "/admin/rpl-client",
    apiBasePath: "rpl",
    deletePermission: P.RPL_CLIENT.DELETE,
    deleteItemLabel: "RPL Record",
    addLabel: "Add RPL Client",
    newEntryLabel: "New RPL certificate",
    downloadType: "rpl",
    clientsQueryKey: "rpl-clients-page",
    recordsQueryKey: "rpl-client-records",
    fetchClientsPage: fetchRplClientsPage,
    fetchRecordsByClientId: fetchRplRecordsByClientId,
    taskModule: "RPL",
    taskEntryType: "RPL",
    listTabs: RPL_STATUS_TABS,
    listTabParamKey: "status",
    entryTabs: RPL_STATUS_TABS,
    entryTabField: "status",
    getEntryTitle: (item) => item?.certificate || "Certificate not set",
    getEntrySubtitle: (item, client) => {
      const parts = [
        item?.usiNumber ? `USI ${item.usiNumber}` : null,
        item?.reference ? `Ref: ${item.reference}` : null,
        item?.visa ? `Applied visa: ${item.visa}` : null,
        // One officer per client: prefer the client's over the entry-level copy.
        client?.caseOfficer || item?.caseOfficer || null,
      ].filter(Boolean);
      return parts.length ? parts.join(" · ") : null;
    },
    getEntryStatus: (item) => cleanStatus(item?.status),
    getEntryDate: formatEntryDate,
    matchesEntryTab: (item, tab) => !tab || tab === "All" || item?.status === tab,
  },
  visa: {
    key: "visa",
    title: "Visa · Clients",
    subtitle: "Open a client to see all their visa applications.",
    entryCountLabel: "Visa applications",
    entriesSectionLabel: "Visa applications",
    basePath: "/admin/visa",
    apiBasePath: "visa-applications",
    deletePermission: P.VISA.DELETE,
    deleteItemLabel: "Visa Application",
    addLabel: "Add Visa",
    hasDocumentTemplates: true,
    newEntryLabel: "New visa application",
    downloadType: "visa",
    clientsQueryKey: "visa-clients-page",
    recordsQueryKey: "visa-client-records",
    fetchClientsPage: fetchVisaClientsPage,
    fetchRecordsByClientId: fetchVisaRecordsByClientId,
    taskModule: "Visa",
    taskEntryType: "VISA",
    listTabs: visaTabs.map((t) => t.value),
    listTabParamKey: "type",
    listTabLabels: visaTabs.reduce((acc, t) => ({ ...acc, [t.value]: t.label }), {}),
    entryTabs: visaTabs.map((t) => t.value),
    entryTabField: "type",
    getEntryTitle: (item) => item?.visa || item?.type || item?.certificate || "Visa application",
    getEntrySubtitle: (item) => {
      const parts = [item?.visa && item?.type ? item.type : null, item?.certificate].filter(
        Boolean
      );
      return parts.length ? parts.join(" · ") : null;
    },
    getEntryStatus: (item) => item?.type || cleanStatus(item?.status),
    getEntryDate: formatEntryDate,
    matchesEntryTab: (item, tab) => matchesVisaTab(item, tab),
  },
  insurance: {
    key: "insurance",
    title: "Insurance · Clients",
    subtitle: "Open a client to see all their insurance policies.",
    entryCountLabel: "Policies",
    entriesSectionLabel: "Insurance policies",
    basePath: "/admin/insurance",
    apiBasePath: "insurance",
    deletePermission: P.INSURANCE.DELETE,
    deleteItemLabel: "Insurance Policy",
    addLabel: "Add Insurance",
    newEntryLabel: "New insurance policy",
    downloadType: "insurance",
    clientsQueryKey: "insurance-clients-page",
    recordsQueryKey: "insurance-client-records",
    fetchClientsPage: fetchInsuranceClientsPage,
    fetchRecordsByClientId: fetchInsuranceRecordsByClientId,
    taskModule: "Insurance",
    taskEntryType: "INSURANCE",
    getEntryTitle: (item) => item?.insuranceCompany || item?.type || "Insurance policy",
    getEntrySubtitle: (item) => {
      const parts = [
        item?.coverType,
        item?.visa ? `Applied visa: ${item.visa}` : null,
        cleanStatus(item?.status),
      ].filter(Boolean);
      return parts.length ? parts.join(" · ") : null;
    },
    getEntryStatus: (item) => cleanStatus(item?.status),
    getEntryDate: formatEntryDate,
    matchesEntryTab: () => true,
  },
  skill: {
    key: "skill",
    title: "Skill Assessment · Clients",
    subtitle: "Open a client to see all their skill assessment records.",
    entryCountLabel: "Assessments",
    entriesSectionLabel: "Skill assessments",
    basePath: "/admin/skill-assessment",
    apiBasePath: "skill-assessment",
    deletePermission: P.SKILL.DELETE,
    deleteItemLabel: "Skill Assessment",
    addLabel: "Add Skill Assessment",
    newEntryLabel: "New skill assessment",
    downloadType: "skill",
    clientsQueryKey: "skill-clients-page",
    recordsQueryKey: "skill-client-records",
    fetchClientsPage: fetchSkillClientsPage,
    fetchRecordsByClientId: fetchSkillRecordsByClientId,
    taskModule: "Skill Assessments",
    taskEntryType: "SKILL_ASSESSMENT",
    getEntryTitle: (item) => item?.occupation || "Skill assessment",
    getEntrySubtitle: (item) => {
      const parts = [
        item?.courseProvider,
        cleanStatus(item?.workflowStatus) ||
          cleanStatus(item?.clientStatus) ||
          cleanStatus(item?.status),
      ].filter(Boolean);
      return parts.length ? parts.join(" · ") : null;
    },
    getEntryStatus: (item) =>
      cleanStatus(item?.workflowStatus) ||
      cleanStatus(item?.clientStatus) ||
      cleanStatus(item?.status),
    getEntryDate: formatEntryDate,
    matchesEntryTab: () => true,
  },
};

export const getModuleClientConfig = (key) => MODULE_CLIENT_CONFIGS[key] ?? null;
